<template>
  <div>
    <h1 class="h1">Even-groove examples</h1>
    <br />

    <div class="input-button-container">
      <div class="input-group container input-wrapper text-center">
        <!-- Tempo -->
        <input
          class="form-control custom-input"
          id="tempoInput"
          type="number"
          v-model="tempo"
          min="30"
          max="250"
          @input="updateTempo"
          title="Enter the bpm"
        />
        <button
          style="width: 25px"
          class="btn custom-plus-and-minus-btn btn-sm"
          @click="incrementTempo"
        >
          +
        </button>
        <button
          style="width: 25px"
          class="btn custom-plus-and-minus-btn btn-sm"
          @click="decrementTempo"
        >
          -
        </button>
        <!-- Steps -->
        <input
          class="form-control custom-input"
          type="number"
          v-model="steps"
          @click="stop()"
          min="8"
          title="Enter the steps"
        />
        <button
          style="width: 25px"
          class="btn custom-plus-and-minus-btn btn-sm"
          @click="incrementSteps"
        >
          +
        </button>
        <button
          style="width: 25px"
          class="btn custom-plus-and-minus-btn btn-sm"
          @click="decrementSteps"
        >
          -
        </button>

        <!-- Play button -->
        <button
          class="btn custom-color-btn"
          id="playButton"
          @click="play()"
          type="button"
        >
          play
        </button>

        <button
          @click="selectNextPattern()"
          class="btn custom-color-btn"
          title="Change the number of beats in the top layer"
        >
          ▲
        </button>

        <!-- Variations button -->
        <button
          @click="selectNextPatternBot()"
          class="btn custom-color-btn"
          title="Change the number of beats in the bottom layer"
        >
          ▼
        </button>
      </div>
    </div>

    <!-- Sequencer -->
    <div
      class="drum-sequencer-container"
      style="margin-top: 2.5em; margin-bottom: -1.95em"
    >
      <div class="drum-sequencer">
        <button
          v-for="(step, index) in this.hh"
          :key="index"
          @click="toggleHihatPulse(index)"
          :class="{
            active: step === 1,
            tick: tick && step === 1,
            'playing-step': isPlayingStep(index),
          }"
        ></button>
      </div>
    </div>
    <div class="drum-sequencer-container" style="margin-bottom: -1.95em">
      <div class="drum-sequencer">
        <button
          v-for="(step, index) in this.sn"
          :key="index"
          @click="toggleSnarePulse(index)"
          :class="{
            active: step === 1,
            tick: tick && step === 1,
            'playing-step': isPlayingStep(index),
          }"
        ></button>
      </div>
    </div>
    <div class="drum-sequencer-container">
      <div class="drum-sequencer">
        <button
          v-for="(step, index) in this.bd"
          :key="index"
          @click="toggleBassPulse(index)"
          :class="{
            active: step === 1,
            tick: tick && step === 1,
            'playing-step': isPlayingStep(index),
          }"
        ></button>
      </div>
    </div>
    <div id="beat-info">
      <code>
        {{ this.showValues() }}
      </code>
    </div>
    <p class="body" style="margin-top: 2.5em">
      This drum-machine uses a non-Western approach to time-playing based on the
      "evenness" of rhythms, and was created in response to the findings
      discussed

      <a
        class="link-danger link-offset-2 link-underline-opacity-25 link-underline-opacity-100-hover"
        href="https://jagrg.gitlab.io/2023-05-05_maximally-even-grooves.html"
        >here</a
      >.
    </p>

    <p class="body">
      All rhythms are made up of two layers. The top layer is the hi-hat,
      followed by the snare and bass drum as the bottom layer. The number of
      beats in the top layer is always greater than the number of steps divided
      by 2, and the beats are distributed as evenly as possible using the
      <code>bjorklund</code> algorithm.
    </p>

    <p class="body">
      The bottom layer is comprised of two sounds (bass and snare) and includes
      less beats than the top layer. All beats in the bottom layer are also
      distributed evenly, except those which are closer to a backbeat position
      are moved to the middle layer (snare). Press the ▲ button to change the
      number of beats in the top layer, and the ▼ button to change the bottom
      layer.
    </p>

    <footer class="text-center" style="margin-top: 2.5em">
      <div style="margin-bottom: 0.75em">
        <a
          class="colored-underline link-dark text-decoration-none"
          href="https://sr.ht/~jagrg/drumbeats/"
          >MIT License</a
        >
      </div>
    </footer>
  </div>
</template>

<script>
import * as Tone from "tone";

export default {
  name: "DrumBeats",
  data() {
    return {
      tempo: 100,
      steps: 14,
      tick: false,
      topVar: 0,
      botVar: 1,
      playingStep: -1,
    };
  },
  mounted() {
    // Run commands from the keyboard
    document.addEventListener("keydown", this.handleKeyDown);
  },
  computed: {
    hh() {
      return this.hihat(this.steps)[this.topVar];
    },
    sn() {
      return this.getSnareDrum(this.steps)[this.botVar];
    },
    bd() {
      return this.getBassDrum(this.steps)[this.botVar];
    },
  },
  methods: {
    incrementTempo() {
      this.tempo++;
      Tone.Transport.bpm.value = this.tempo;
    },
    decrementTempo() {
      this.tempo--;
      Tone.Transport.bpm.value = this.tempo;
    },
    incrementSteps() {
      this.steps++;
      if (Tone.Transport.state === "started") {
        Tone.Transport.stop();
        Tone.Transport.cancel(0);
      }
      this.play();
    },
    decrementSteps() {
      if (this.steps > 8) {
        this.steps--;
        if (Tone.Transport.state === "started") {
          Tone.Transport.stop();
          Tone.Transport.cancel(0);
        }
        this.play();
      }
    },
    showValues() {
      const topLayerPulses = this.getPulses(this.hh);
      const botLayerPulses = this.getPulses(this.sn) + this.getPulses(this.bd);

      if (topLayerPulses !== undefined && botLayerPulses !== undefined) {
        return `${topLayerPulses},${botLayerPulses}`;
      }
    },
    handleKeyDown(event) {
      const playButton = document.getElementById("playButton");
      const key = event.key;
      switch (key) {
        case "Enter":
          if (document.activeElement !== playButton) {
            this.play();
          }
          break;
      }
    },
    updateTempo() {
      const tempoInput = document.getElementById("tempoInput");
      this.tempo = tempoInput.value;
      Tone.Transport.bpm.value = this.tempo;
    },
    stop() {
      let playButton = document.getElementById("playButton");
      if (Tone.Transport.state === "started") {
        Tone.Transport.stop();
        Tone.Transport.cancel(0);
        Tone.Transport.position = "0";
        playButton.innerText = "play";
      }
    },
    isPlayingStep(stepIndex) {
      return this.playingStep === stepIndex;
    },
    getPulses(array) {
      if (array) {
        const count = array.reduce((accumulator, currentValue) => {
          if (currentValue === 1) {
            return accumulator + 1;
          } else {
            return accumulator;
          }
        }, 0);

        return count;
      }
    },
    selectNextPattern() {
      const variations = this.hihat(this.steps).length;
      if (this.topVar === variations - 1) {
        this.topVar = 0;
      } else {
        this.topVar++;
      }
      if (Tone.Transport.state === "started") {
        Tone.Transport.stop();
        Tone.Transport.cancel(0);
      }
      this.play();
      // console.log(this.topVar);
    },
    selectNextPatternBot() {
      const variations = this.getBassDrum(this.steps).length;
      if (this.botVar === variations - 1) {
        this.botVar = 0;
      } else {
        this.botVar++;
      }
      if (Tone.Transport.state === "started") {
        Tone.Transport.stop();
        Tone.Transport.cancel(0);
      }
      this.play();
      // console.log(this.botVar);
    },
    toggleHihatPulse(index) {
      this.hh[index] = this.hh[index] === 1 ? 0 : 1;
    },
    toggleSnarePulse(index) {
      this.sn[index] = this.sn[index] === 1 ? 0 : 1;
    },
    toggleBassPulse(index) {
      this.bd[index] = this.bd[index] === 1 ? 0 : 1;
    },
    play() {
      const channel0 = new Tone.Channel().toDestination();
      const channel1 = new Tone.Channel().toDestination();
      const channel2 = new Tone.Channel().toDestination();

      const players = new Tone.Players({
        urls: {
          0: "hihat.mp3",
          1: "snare.mp3",
          2: "kick.mp3",
        },
        baseUrl: "https://tonejs.github.io/audio/drum-samples/breakbeat8/",
      });

      players.player("0").connect(channel0);
      players.player("1").connect(channel1);
      players.player("2").connect(channel2);

      const pattern = [this.hh, this.sn, this.bd];
      let stepRef = 0;

      const schedulePattern = () => {
        Tone.Transport.scheduleRepeat((time) => {
          pattern.forEach((row, index) => {
            const palyer = players.player(index.toString());
            let note = row[stepRef];
            if (note === 1) {
              Tone.loaded().then(() => {
                palyer.start(time);
              });
            }
          });
          this.playingStep = stepRef;
          stepRef = (stepRef + 1) % pattern[0].length;
        }, "16n");
      };

      Tone.Transport.bpm.value = this.tempo;

      const stop = () => {
        Tone.Transport.stop();
        Tone.Transport.cancel(0);
        stepRef = 0;
      };

      const start = () => {
        schedulePattern();
        Tone.Transport.start();
        Tone.Transport.position = "0";
      };

      if (Tone.Transport.state === "started") {
        stop();
        let playButton = document.getElementById("playButton");
        playButton.innerText = "play";
      } else {
        Tone.start();
        start();
        let playButton = document.getElementById("playButton");
        playButton.innerText = "stop";
      }
    },
    // Generate an Euclidean rhythm from the Bjorklund algorithm
    bjorklund(pulses, steps) {
      if (pulses < 0 || steps < 0 || steps < pulses) {
        return [];
      }
      // Create the two arrays
      let head = new Array(pulses).fill([1]);
      let remainders = new Array(steps - pulses).fill([0]);
      // Pick the shortest one
      let minLength = Math.min(head.length, remainders.length);
      let loopThreshold = 0;

      while (minLength > loopThreshold) {
        // Allow only loopThreshold to be zero on the first loop
        if (loopThreshold === 0) {
          loopThreshold = 1;
        }
        for (let i = 0; i < minLength; i++) {
          head[i] = Array.prototype.concat.call(head[i], remainders[i]);
        }

        if (minLength === head.length) {
          // remainders[minLength -> end]
          remainders = Array.prototype.slice.call(remainders, minLength);
        } else {
          remainders = Array.prototype.slice.call(head, minLength);
          head = Array.prototype.slice.call(head, 0, minLength);
        }
        minLength = Math.min(head.length, remainders.length);
      }

      let sequence = [];
      sequence = Array.prototype.concat.call(head, remainders).flat();
      return sequence;
    },
    // List all pulses suitable for the hihat
    listHihatPulses(steps) {
      const pulses = [];
      const start = Math.ceil(steps / 2);

      for (let i = start; i < steps; i++) {
        pulses.push(i);
      }

      return pulses;
    },
    // List all pulses suitable for the bass drum
    listBassDrumPulses(steps) {
      const pulses = [];
      const end = Math.floor(steps / 2);

      for (let i = 3; i <= end; i++) {
        pulses.push(i);
      }

      if (pulses.length > 0) {
        return pulses;
      } else {
        return [1];
      }
    },
    // Return true if sublist contains 5 or more conscutive ones
    consecutiveOnesP(sublist) {
      let count = 0;

      for (const element of [...sublist, ...sublist]) {
        if (element === 1) {
          count++;
        } else {
          count = 0;
        }

        if (count > 4) {
          return true;
        }
      }

      return false;
    },
    // Remove all sublists not suitable for the hihat
    removeConsecutiveOnes(list) {
      return list.filter((sublist) => !this.consecutiveOnesP(sublist));
    },
    // Generate all hihat rhythms from STEPS
    hihat(steps) {
      const hihatRhythms = [];
      const hihatPulses = this.listHihatPulses(steps);

      for (const pulse of hihatPulses) {
        hihatRhythms.push(this.bjorklund(pulse, steps));
      }

      return this.removeConsecutiveOnes(hihatRhythms);
    },
    // Generate all bass drum rhythms from STEPS
    bassDrum(steps) {
      const bassDrumRhythms = [];
      const bassDrumPulses = this.listBassDrumPulses(steps);

      for (const pulse of bassDrumPulses) {
        bassDrumRhythms.push(this.bjorklund(pulse, steps));
      }

      return this.removeConsecutiveOnes(bassDrumRhythms);
    },
    // Return a list of backbeat positions
    calculateBackbeatPositions(steps) {
      let pattern = this.bjorklund(2, steps);

      if (steps % 3 === 0) {
        pattern = pattern.slice(3).concat(pattern.slice(0, 3));
      } else {
        pattern = pattern.slice(4).concat(pattern.slice(0, 4));
      }

      if (pattern[0] === 1) {
        pattern[0] = 0;
      }

      const backbeatPositions = [];
      for (let index = 0; index < pattern.length; index++) {
        if (pattern[index] === 1) {
          backbeatPositions.push(index);
        }
      }

      return backbeatPositions;
    },
    findNearest(target, list) {
      if (list.length > 0) {
        let nearest = list[0];
        let diff = Math.abs(target - list[0]);

        for (let i = 1; i < list.length; i++) {
          const element = list[i];
          const currentDiff = Math.abs(target - element);

          if (currentDiff < diff) {
            nearest = element;
            diff = currentDiff;
          }
        }

        return nearest;
      }

      return null; // Or appropriate value when the list is empty
    },
    makeRhythm(steps, list) {
      // Calculate backbeat positions and replace the onsets closest to backbeat
      // index positions.
      const backbeats = this.calculateBackbeatPositions(steps);

      // Collect onset positions
      const onsets = [];
      for (let i = 0; i < list.length; i++) {
        if (list[i] === 1) {
          onsets.push(i);
        }
      }

      // Create a copy of the list
      const modifiedList = list.slice();

      // Iterate through backbeats and update modifiedList
      for (const backbeat of backbeats) {
        const nearestOnset = this.findNearest(backbeat, onsets);
        if (nearestOnset !== null) {
          modifiedList[nearestOnset] = 2;
        }
      }

      return modifiedList;
    },
    makeRhythms(steps) {
      const patterns = [];

      this.bassDrum(steps).forEach((pattern) => {
        patterns.push(this.makeRhythm(steps, pattern));
      });

      return patterns;
    },
    // FIXME: Fails if step value is less than 8
    getBassDrum(steps) {
      const patterns = this.makeRhythms(steps);
      const list = [];

      patterns.forEach((pattern) => {
        list.push(pattern.map((x) => (x === 2 ? 0 : x)));
      });

      return list;
    },
    getSnareDrum(steps) {
      const patterns = this.makeRhythms(steps);
      const list = [];

      patterns.forEach((pattern) => {
        list.push(
          pattern.map((x) => {
            if (x === 1) {
              return 0;
            } else if (x === 2) {
              return 1;
            } else {
              return x;
            }
          })
        );
      });

      return list;
    },
  },
};
</script>

<style scoped>
.drum-sequencer-container {
  display: flex;
  justify-content: center;
  margin-top: 1.95rem;
}

.drum-sequencer {
  display: flex;
  gap: 0;
}

.drum-sequencer button {
  height: 35px;
  width: 35px;
  background-color: #fff;
  border: 0.5px solid #777;
}

/* James Koetting's tubs notation */
.drum-sequencer button.active {
  position: relative;
}

.drum-sequencer button.active::before {
  content: "";
  display: block;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  background-color: #333;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.h1 {
  font-family: "DejaVu Sans", "Open Sans", "Noto Sans", system-ui, sans-serif;
  margin-bottom: 1.25rem;
  font-size: 28px;
  letter-spacing: 1.5px;
  margin-top: 2.5rem;
  margin-bottom: 2.5rem;
  text-align: center;
}

.h2 {
  font-family: "DejaVu Sans", "Open Sans", "Noto Sans", system-ui, sans-serif;
  font-size: 20px;
  letter-spacing: 1.5px;
  margin-top: 2.25rem;
  text-align: center;
}

.custom-color-btn {
  color: #333;
  border-color: #bbb;
  background-color: #666;
  color: #ffcc00;
}

.custom-color-btn:hover,
.custom-color-btn:focus {
  color: #666;
  border-color: #bbb;
  background-color: #ffcc00;
}

.custom-plus-and-minus-btn {
  color: #000;
  border-color: #bbb;
  font-family: monospace;
}

.custom-plus-and-minus-btn:hover,
.custom-plus-and-minus-btn:focus {
  color: #666;
  border-color: #bbb;
  background-color: #ffcc00;
  font-family: monospace;
}

.input-wrapper {
  display: flex;
  justify-content: center;
}

.custom-input {
  max-width: 85px;
}

.drum-sequencer button.playing-step {
  background-color: #ffcc00;
}

.body {
  padding-left: 25%;
  padding-right: 25%;
  font-size: 18px;
}

@media (max-width: 768px) {
  .body {
    padding-left: 2%;
    padding-right: 2%;
  }
}

.colored-underline {
  position: relative;
  display: inline-block;
  padding-bottom: 4px;
  background-image: linear-gradient(to right, #ffcc00, #ffcc00);
  background-size: 100% 3px;
  background-repeat: no-repeat;
  background-position: 0 100%;
}

#beat-info {
  text-align: center;
  margin-top: 1.15em;
  font-size: 20px;
}
</style>
